import { forwardRef } from 'react'
import { IconButton } from '~/shared/ui/icon-button'
import { LocalIcon } from '~/shared/ui/local-icon'
import { Tooltip } from '~/shared/ui/local-tooltip'
import { Text } from '~/shared/ui/text'

export interface MediaViewerControlProps {
  pageText: string
  onPageTextChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  totalPagesToDisplay: number
  fitToWidth: boolean
  onFitToWidth: () => void
  onZoomOut: () => void
  onZoomIn: () => void
  zoomOutDisabled: boolean
  zoomInDisabled: boolean
  onDownload?: () => void
  onExpandContent?: () => void
  onMinimizeContent?: () => void
  onOpenInNewTab?: () => void
  onRotateLeft?: () => void
  onRotateRight?: () => void
  onInteraction?: (type: string) => void
  scale?: number
}

export const MediaViewerControl = forwardRef<HTMLDivElement, MediaViewerControlProps>(
  function MediaViewerControl(
    {
      pageText,
      onPageTextChange,
      totalPagesToDisplay,
      fitToWidth,
      onFitToWidth,
      onZoomOut,
      onZoomIn,
      zoomOutDisabled,
      zoomInDisabled,
      onDownload,
      onExpandContent,
      onMinimizeContent,
      onOpenInNewTab,
      onRotateLeft,
      onRotateRight,
      onInteraction,
      scale = 100,
    },
    ref,
  ) {
    if (totalPagesToDisplay === 0) {
      return null
    }

    return (
      <div
        ref={ref}
        className="sticky bottom-5 z-5 flex h-0 justify-center"
        style={{ transform: `scale(${scale / 100})` }}
      >
        <div className="bg-content-highest-emphasis text-content-highest-emphasis-inverse absolute flex h-[56px] w-auto -translate-y-full items-center gap-2 rounded-xl px-4 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <Text appearance="BODY2">
            <div className="whitespace-nowrap">
              <span className="mr-3">Page</span>
              <input
                className="border-content-medium-emphasis-inverse box-border h-9 w-[44px] rounded-sm border bg-transparent px-3 text-center text-inherit outline-none"
                value={pageText}
                onChange={onPageTextChange}
              />
              <span className="px-3">/</span>
              <span className="px-3">{totalPagesToDisplay}</span>
            </div>
          </Text>
          <div className="bg-content-medium-emphasis-inverse mx-2 h-full w-px" />
          <div className="flex gap-2">
            <Tooltip
              content="Zoom out"
              sideOffset={4}
              disableHoverableContent
            >
              <IconButton
                className="text-inherit disabled:opacity-38"
                size="medium"
                container="small"
                materialIcon="remove"
                onClick={() => {
                  onZoomOut()
                  onInteraction?.('zoom_out')
                }}
                disabled={zoomOutDisabled}
              />
            </Tooltip>
            <Tooltip
              content="Fit to width"
              sideOffset={4}
              disableHoverableContent
            >
              <IconButton
                className="text-inherit disabled:opacity-38"
                size="medium"
                container="small"
                materialIcon={fitToWidth ? 'zoom_out' : 'zoom_in'}
                onClick={() => {
                  onFitToWidth()
                  onInteraction?.('fit_to_width')
                }}
              />
            </Tooltip>
            <Tooltip
              content="Zoom in"
              sideOffset={4}
              disableHoverableContent
            >
              <IconButton
                className="text-inherit disabled:opacity-38"
                size="medium"
                container="small"
                materialIcon="add"
                onClick={() => {
                  onZoomIn()
                  onInteraction?.('zoom_in')
                }}
                disabled={zoomInDisabled}
              />
            </Tooltip>
          </div>
          <div className="bg-content-medium-emphasis-inverse mx-2 h-full w-px" />
          {(onRotateLeft || onRotateRight) && (
            <>
              {onRotateLeft && (
                <Tooltip
                  content="Rotate left"
                  sideOffset={4}
                  disableHoverableContent
                >
                  <IconButton
                    className="text-inherit"
                    size="medium"
                    container="small"
                    onClick={() => {
                      onRotateLeft?.()
                      onInteraction?.('rotate_left')
                    }}
                  >
                    <LocalIcon name="rotateLeft" />
                  </IconButton>
                </Tooltip>
              )}
              {onRotateRight && (
                <Tooltip
                  content="Rotate right"
                  sideOffset={4}
                  disableHoverableContent
                >
                  <IconButton
                    className="text-inherit"
                    size="medium"
                    container="small"
                    onClick={() => {
                      onRotateRight?.()
                      onInteraction?.('rotate_right')
                    }}
                  >
                    <LocalIcon name="rotateRight" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          {onExpandContent && (
            <Tooltip
              content="Fullscreen"
              sideOffset={4}
              disableHoverableContent
            >
              <IconButton
                className="text-inherit"
                size="medium"
                container="small"
                materialIcon="open_in_full"
                onClick={onExpandContent}
              />
            </Tooltip>
          )}
          {onOpenInNewTab && (
            <Tooltip
              content="Open in new tab"
              sideOffset={4}
              disableHoverableContent
            >
              <IconButton
                className="text-inherit"
                size="medium"
                container="small"
                materialIcon="open_in_new"
                onClick={() => {
                  onOpenInNewTab?.()
                  onInteraction?.('open_in_new_tab')
                }}
              />
            </Tooltip>
          )}
          {onMinimizeContent && (
            <Tooltip
              content="Exit fullscreen"
              sideOffset={4}
              disableHoverableContent
            >
              <IconButton
                className="text-inherit"
                size="medium"
                container="small"
                materialIcon="close_fullscreen"
                onClick={onMinimizeContent}
              />
            </Tooltip>
          )}
          {onDownload && (
            <>
              <div className="bg-content-medium-emphasis-inverse mx-2 h-full w-px" />
              <Tooltip
                content="Download document"
                sideOffset={-6}
                disableHoverableContent
              >
                <IconButton
                  className="text-inherit"
                  size="medium"
                  container="medium"
                  materialIcon="download"
                  onClick={onDownload}
                />
              </Tooltip>
            </>
          )}
        </div>
      </div>
    )
  },
)
